import React from 'react';
import { motion } from 'framer-motion';
import { LayoutGrid, MessageSquare, BarChart3, Calendar } from 'lucide-react';
// A sharp problem-statement beat that sets up the connected-stack payoff.
// Redesigned as a two-column split with a "chaos" visual (overlapping tabs)
// to contrast with the "calm" visual in the following section.
export function TwelveTabProblem() {
  return <section className="relative w-full overflow-hidden bg-[#FAF8F4] py-24 md:py-32 border-t border-neutral-200/70" data-id="element-3020">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center" data-id="element-3021">
        {/* Text (Left) */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative z-10 max-w-xl" data-id="element-3022">
          <h2 className="text-3xl md:text-5xl font-title font-medium leading-[1.1] tracking-tight text-[#2B211A]" data-id="element-3023">
            Stop living in 11 tabs.
          </h2>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-neutral-500 font-medium leading-relaxed" data-id="element-3024">
            Every context switch costs you 23 minutes of focus. Giga works
            across your stack so you don't have to.
          </p>
        </motion.div>

        {/* Visual (Right) - Chaos */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative h-[360px] md:h-[440px] w-full flex items-center justify-center" data-id="element-3025">
          {/* Soft background glow */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(234,88,12,0.06)_0%,transparent_70%)]" data-id="element-3026" />

          {/* Floating App Cards (The 11-tab mess) */}
          <div className="relative w-full max-w-[340px] aspect-square" data-id="element-3027">
            {/* App 1: Calendar */}
            <motion.div animate={{
            y: [0, -8, 0],
            rotate: [-6, -6, -6]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-[10%] left-[5%] w-48 bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-neutral-200/60 p-4 z-10" data-id="element-3028">
              <div className="flex items-center gap-2 mb-3" data-id="element-3029">
                <div className="w-6 h-6 rounded bg-blue-50 text-blue-500 flex items-center justify-center" data-id="element-3030">
                  <Calendar size={12} data-id="element-3031" />
                </div>
                <div className="h-2 w-16 bg-neutral-200 rounded" data-id="element-3032" />
              </div>
              <div className="space-y-2" data-id="element-3033">
                <div className="h-1.5 w-full bg-neutral-100 rounded" data-id="element-3034" />
                <div className="h-1.5 w-4/5 bg-neutral-100 rounded" data-id="element-3035" />
              </div>
            </motion.div>

            {/* App 2: Chat */}
            <motion.div animate={{
            y: [0, 10, 0],
            rotate: [8, 8, 8]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute top-[25%] right-[0%] w-52 bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-neutral-200/60 p-4 z-20" data-id="element-3036">
              <div className="flex items-center gap-2 mb-3" data-id="element-3037">
                <div className="w-6 h-6 rounded bg-purple-50 text-purple-500 flex items-center justify-center" data-id="element-3038">
                  <MessageSquare size={12} data-id="element-3039" />
                </div>
                <div className="h-2 w-20 bg-neutral-200 rounded" data-id="element-3040" />
              </div>
              <div className="space-y-2" data-id="element-3041">
                <div className="h-6 w-full bg-neutral-50 rounded" data-id="element-3042" />
                <div className="h-6 w-3/4 bg-neutral-50 rounded" data-id="element-3043" />
              </div>
            </motion.div>

            {/* App 3: CRM / Data */}
            <motion.div animate={{
            y: [0, -5, 0],
            rotate: [-12, -12, -12]
          }} transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5
          }} className="absolute bottom-[15%] left-[15%] w-56 bg-white rounded-xl shadow-[0_12px_40px_rgb(0,0,0,0.08)] border border-neutral-200/60 p-4 z-30" data-id="element-3044">
              <div className="flex items-center gap-2 mb-3" data-id="element-3045">
                <div className="w-6 h-6 rounded bg-orange-50 text-orange-500 flex items-center justify-center" data-id="element-3046">
                  <BarChart3 size={12} data-id="element-3047" />
                </div>
                <div className="h-2 w-24 bg-neutral-200 rounded" data-id="element-3048" />
              </div>
              <div className="grid grid-cols-3 gap-2" data-id="element-3049">
                <div className="h-12 bg-neutral-50 rounded" data-id="element-3050" />
                <div className="h-12 bg-neutral-50 rounded" data-id="element-3051" />
                <div className="h-12 bg-neutral-50 rounded" data-id="element-3052" />
              </div>
            </motion.div>

            {/* App 4: Project Management */}
            <motion.div animate={{
            y: [0, 6, 0],
            rotate: [4, 4, 4]
          }} transition={{
            duration: 5.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.5
          }} className="absolute bottom-[5%] right-[10%] w-48 bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-neutral-200/60 p-4 z-40" data-id="element-3053">
              <div className="flex items-center gap-2 mb-3" data-id="element-3054">
                <div className="w-6 h-6 rounded bg-green-50 text-green-500 flex items-center justify-center" data-id="element-3055">
                  <LayoutGrid size={12} data-id="element-3056" />
                </div>
                <div className="h-2 w-16 bg-neutral-200 rounded" data-id="element-3057" />
              </div>
              <div className="space-y-2" data-id="element-3058">
                <div className="flex items-center gap-2" data-id="element-3059">
                  <div className="w-3 h-3 rounded-full border border-neutral-300" data-id="element-3060" />
                  <div className="h-1.5 w-24 bg-neutral-100 rounded" data-id="element-3061" />
                </div>
                <div className="flex items-center gap-2" data-id="element-3062">
                  <div className="w-3 h-3 rounded-full border border-neutral-300" data-id="element-3063" />
                  <div className="h-1.5 w-16 bg-neutral-100 rounded" data-id="element-3064" />
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}